import { sortFilesWithMainImageFirst } from './sortFilesWithMainImageFirst';
import type { DocumentOnView } from 'presentation/document/documentOnView';
import type { MainImage } from '../mainImage';
import {
  inferFileStateKind,
  isUnsavedFileType,
  type FileReadSuccess,
  type UploaderFile,
} from './types';
import { MAX_FILES } from 'shared/domain/document/documentLimits';
import { ResultType } from './types';
import { getCachedResolvedPreviewState } from './previewUrlsCache';
import { isPlainObject } from 'shared/utils/object';
import { isBlobUrl } from 'shared/utils/blobUrl';
import { DOCUMENT_ERROR_SOURCE } from 'shared/domain/document/documentError';

function hasLocalBlobPreview(file: {
  src?: string;
  downloadSrc?: string;
  thumbnail?: string;
}): boolean {
  return Boolean(
    (typeof file.src === 'string' &&
      isBlobUrl(file.src) &&
      file.src !== DOCUMENT_ERROR_SOURCE) ||
      (typeof file.downloadSrc === 'string' && isBlobUrl(file.downloadSrc)) ||
      (typeof file.thumbnail === 'string' && isBlobUrl(file.thumbnail))
  );
}

export function sameFileKey(a: unknown): string {
  if (!isPlainObject(a)) {
    return '|';
  }

  const titleValue = Reflect.get(a, 'title');
  const dataValue = Reflect.get(a, 'data');
  const sizeValue = Reflect.get(a, 'size');

  const dataName =
    isPlainObject(dataValue)
      ? Reflect.get(dataValue, 'name')
      : undefined;

  const name =
    typeof titleValue === 'string'
      ? titleValue
      : typeof dataName === 'string'
      ? dataName
      : '';

  return `${name}|${typeof sizeValue === 'number' ? sizeValue : ''}`;
}

function uniqueKeyForDedup(a: unknown): string {
  if (isPlainObject(a)) {
    const idValue = Reflect.get(a, '_id');
    if (typeof idValue === 'string' && idValue.length > 0) {
      return `id:${idValue}`;
    }
  }

  return `key:${sameFileKey(a)}`;
}

export function deduplicateFileList<
  T extends UploaderFile<DocumentOnView | FileReadSuccess>,
>(files: T[]): T[] {
  const seen = new Set<string>();
  return files.filter((f) => {
    const key = uniqueKeyForDedup(f);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function filterOutDuplicatesOfNewFiles<
  T extends UploaderFile<DocumentOnView | FileReadSuccess>,
>(existing: T[], newFiles: FileReadSuccess[]): T[] {
  const newKeys = new Set(newFiles.map((f) => sameFileKey(f)));
  return existing.filter((f) => !newKeys.has(sameFileKey(f)));
}

export function preventOverflow<T>(
  oldFilesLength: number,
  newFiles: T[]
): T[] {
  if (oldFilesLength > MAX_FILES) {
    return [];
  }
  return newFiles.slice(0, MAX_FILES - oldFilesLength);
}

type FileWithLoading = FileReadSuccess & {
  loading: boolean;
  _id?: string;
};

function sortWithMainFile<T extends Omit<FileReadSuccess, 'resultType'>>(
  files: (T & FileReadSuccess & { loading: boolean; _id?: string })[],
  mainImageHandler?: MainImage
): (T & FileReadSuccess & { loading: boolean; _id?: string })[] {
  if (!mainImageHandler) return files;
  const mainImageId = mainImageHandler.getMainImageStore().get();
  return sortFilesWithMainImageFirst(files, mainImageId);
}

export function processInitialFiles<
  T extends Omit<FileReadSuccess, 'resultType'>,
>(files: T[], mainImageHandler?: MainImage): (T & FileWithLoading)[] {
  if (!files) {
    return [];
  }
  return sortWithMainFile(
    files.map((file) => {
      const cachedPreview = getCachedResolvedPreviewState(file);
      const signedRequest =
        file.signedRequest ?? cachedPreview?.signedRequest;

      const fileWithOptionalThumbnail = file as T & { thumbnail?: string };
      const downloadSrc = file.downloadSrc ?? cachedPreview?.downloadSrc;
      const src = file.src ?? cachedPreview?.src;
      const thumbnail =
        fileWithOptionalThumbnail.thumbnail ?? cachedPreview?.thumbnail;

      const hasResolvedPreviewSource = Boolean(
        signedRequest ||
          hasLocalBlobPreview({ downloadSrc, src, thumbnail })
      );

      return {
        ...file,
        signedRequest,
        thumbnail,
        downloadSrc,
        src,
        fileStateKind: inferFileStateKind(file),
        resultType: ResultType.success,
        loading: isUnsavedFileType(file) ? false : !hasResolvedPreviewSource,
      };
    }),
    mainImageHandler
  );
}
